import { Emitter } from "@core/events/emitter";
import {
  K_DEPLOYMENTS_MANIFEST_URL,
  parseDeploymentsManifest,
  type DeploymentEntryDto,
} from "@models/data/deploymentsManifestDto";
import type { IDeploymentsModel } from "./IDeploymentsModel";

/**
 * `DeploymentsModel` — the fetch behind {@link IDeploymentsModel}.
 *
 * Headless: one `fetch`, a parse, a cache, and a change emitter. No transport
 * to attach — the manifest is a static file on the same origin, served by nginx
 * with no auth (it names deployments, not their contents), so unlike
 * `AppSettingsModel` this needs neither auth nor storage and can be registered
 * in the core pass complete.
 *
 * `cache: "no-cache"` matches the four AI catalog loaders' idiom and is the
 * point of the file: a tab open across a deploy should see the new row on the
 * next refresh, not a cached copy from before it.
 */
export class DeploymentsModel implements IDeploymentsModel {
  readonly onChanged = new Emitter<void>();

  private m_deployments: readonly DeploymentEntryDto[] = [];
  private m_isLoaded = false;
  private m_lastError: string | null = null;
  private m_loadTask: Promise<void> | null = null;

  get deployments(): readonly DeploymentEntryDto[] {
    return this.m_deployments;
  }

  get isLoaded(): boolean {
    return this.m_isLoaded;
  }

  get isLoading(): boolean {
    return this.m_loadTask !== null;
  }

  get lastError(): string | null {
    return this.m_lastError;
  }

  get current(): DeploymentEntryDto | null {
    const base = import.meta.env.BASE_URL;
    return this.m_deployments.find((entry) => entry.basePath === base) ?? null;
  }

  ensureLoadedAsync(): Promise<void> {
    if (this.m_isLoaded && !this.m_lastError) return Promise.resolve();
    return this.refreshAsync();
  }

  refreshAsync(): Promise<void> {
    // Share the in-flight task: the panel binds and the user can hit Refresh in
    // the same frame, and two concurrent fetches would emit twice for one state.
    if (this.m_loadTask) return this.m_loadTask;
    this.m_loadTask = this.loadAsync().finally(() => {
      this.m_loadTask = null;
    });
    // Emit on the way IN as well as on the way out. Without this `isLoading` is
    // unobservable on a re-fetch — the settle-only emit lands when it is already
    // false again — so the panel could never show that a manual Refresh was
    // running. (The first load happens to be visible either way, because
    // `isLoaded` flips at the same time.)
    this.onChanged.emit();
    return this.m_loadTask;
  }

  private async loadAsync(): Promise<void> {
    try {
      const response = await fetch(K_DEPLOYMENTS_MANIFEST_URL, { cache: "no-cache" });
      if (!response.ok) {
        throw new Error(`HTTP ${String(response.status)}`);
      }
      // A 200 is NOT proof this is the manifest. nginx answers an unknown path
      // with the SPA fallback — index.html, 200, text/html — so a deployment
      // whose server predates the manifest block reaches `response.json()` with
      // "<!doctype html>" and throws. Checking the type first turns that into
      // an honest message instead of a JSON syntax error.
      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("json")) {
        throw new Error("no deployments manifest is published on this origin");
      }
      const manifest = parseDeploymentsManifest(await response.json());
      this.m_deployments = manifest.deployments;
      this.m_lastError = null;
    } catch (error) {
      // Keep whatever was already loaded: a failed refresh should not blank a
      // list that was correct a moment ago.
      this.m_lastError = error instanceof Error ? error.message : String(error);
      console.warn("[deployments] Could not read the deployments manifest:", this.m_lastError);
    } finally {
      this.m_isLoaded = true;
      this.onChanged.emit();
    }
  }
}
